import { memo, useEffect, useState } from "react";
import { TIServiceForm } from "../../iServiceSchema";
import { IServiceType } from "../IServiceType";
import { Certillion } from "./Certillion";
import { useTranslation } from "react-i18next";
import { IIServiceType } from "../../../../../../../models/i-service-type";
import { useFormContext } from "@4uhub/lib4uhub";
import { useIServiceType } from "../useIServiceType";
import { useParams } from "react-router-dom";

interface ICertillionWrapperProps {
  iServiceTypes: IIServiceType[];
}
const CERTILLION_IMAGE = {
  src: "/iServices/certillion.png",
  alt: "Certillion",
};

const translationPath = "page.4uBus.register.iService.certillion.";

export const CertillionWrapper = memo(
  ({ iServiceTypes }: ICertillionWrapperProps) => {
    const { id } = useParams();

    const { t } = useTranslation();

    const { setValue, getValues } = useFormContext<TIServiceForm>();

    const { certillionParser, certillionValues } = useIServiceType();

    const [isSelected, setIsSelected] = useState(() => {
      const certillion = getValues("certillion");
      return !!certillion;
    });

    const parseOptionalNumber = (value?: string | null) => {
      if (!value || value.trim() === "") return undefined;

      const parsedValue = Number(value);

      return Number.isFinite(parsedValue) ? parsedValue : undefined;
    };

    const saveConfigsJson = (v: TIServiceForm) => {
      const values = v.certillion;
      if (!values) return;

      const configs = {
        isCertillionCloud: values.isCertillionCloud,
        authorizeLocal: values.authorizeLocal,
        baseUrl: values.baseUrl,
        baseUrlProxy: values.baseUrlProxy,
        clientId: values.clientId,
        clientSecret: values.clientSecret,
        signatureStandardOptions: values.signatureStandardOptions,
        signatureHorizontalX: parseOptionalNumber(values.signatureHorizontalX),
        signatureHorizontalY: parseOptionalNumber(values.signatureHorizontalY),
        signatureVerticalX: parseOptionalNumber(values.signatureVerticalX),
        signatureVerticalY: parseOptionalNumber(values.signatureVerticalY),
      };

      return configs;
    };

    const handleConfirm = () => {
      const companyDigitalCertificate = getValues(
        "certillion.companyDigitalCertificate",
      );

      const iServiceType = iServiceTypes.find(
        (iServiceType) => iServiceType.code === certillionValues.code,
      );

      if (iServiceType) {
        setValue("iServiceType", iServiceType);
      }

      setValue("companyDigitalCertificate", companyDigitalCertificate);
      setIsSelected(true);
    };

    const handleClearConfigs = () => {
      setValue("configs", null);
      setValue("iServiceType", null);
      setValue("companyDigitalCertificate", null);
      setValue("certillion", certillionValues.defaultValues);
      setIsSelected(false);
    };

    const handleOpen = () => {
      setValue("certillion", certillionValues.defaultValues);
    };

    const handleCloseSelected = () => {
      const configs = getValues("configs");
      const existingCert =
        getValues("certillion.companyDigitalCertificate") ||
        getValues("companyDigitalCertificate");

      const certillionValue = certillionParser(configs, existingCert);

      if (certillionValue) {
        setValue("certillion", certillionValue);
      }
      setValue("companyDigitalCertificate", null);
    };

    useEffect(() => {
      if (isSelected) {
        const configs = getValues("configs");
        const existingCert =
          getValues("certillion.companyDigitalCertificate") ||
          getValues("companyDigitalCertificate");

        const parsedValues = certillionParser(configs, existingCert);

        if (parsedValues) {
          setValue("certillion", parsedValues);
        }
      }
    }, [isSelected, getValues, setValue, certillionParser]);

    const isEditingScreen = !!id;

    return (
      <IServiceType
        img={CERTILLION_IMAGE}
        renderComponent={({ namePrefix }) => (
          <Certillion namePrefix={namePrefix} />
        )}
        isSelected={isSelected}
        isEditingScreen={isEditingScreen}
        getFormattedData={saveConfigsJson}
        onConfirm={handleConfirm}
        onClearConfigs={handleClearConfigs}
        onOpen={handleOpen}
        iServiceTypeName="certillion"
        onCloseSelected={handleCloseSelected}
        serviceName={t(translationPath + "name")}
      />
    );
  },
);
